import { supabase, signOut, getCurrentUser, isSupabaseReady } from '../services/supabase.js';
import { renderNavbar } from '../components/navbar.js';
import { escapeHtml } from '../utils/html.js';
import { renderAvatarInner, sanitizeAvatarEmoji } from '../utils/avatar.js';
import { validatePseudo, PSEUDO_MAX_LENGTH } from '../utils/profile.js';
import { getTheme, setTheme } from '../services/theme.js';
import { formatNumber, getLanguage, setLanguage, supportedLanguages, t, tr } from '../services/i18n.js';
import { GOAL_PRESETS, calculateBasePoints, levelFromPoints, progressForGoal, goalPresetByType } from '../utils/gamification.js';
import { markProfileTutorialSeen, openAppTutorial } from '../components/app-tutorial.js';
import QRCode from 'qrcode';
import { downloadCurrentKeycard, exportCurrentKeycard } from '../services/keycard-auth.js';

const AVATAR_MAX_BYTES = 2 * 1024 * 1024;
const AVATAR_ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const AVATAR_EXT_BY_TYPE = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif'
};

export async function renderProfile(container) {
  if (!isSupabaseReady()) {
    container.innerHTML = `<div class="page p-16 text-center" style="padding-bottom:100px;"><h2 class="heading-2">${t('configRequired')}</h2><p class="text-muted mt-16">${t('addSupabaseEnv')}</p></div>`;
    return;
  }

  container.innerHTML = `
    <div class="page p-16" style="padding-bottom:100px;">
      <div id="profile-content">
        <div class="glass-card skeleton" style="height:120px;margin-bottom:16px;"></div>
        <div class="glass-card skeleton" style="height:80px;"></div>
      </div>
    </div>
  `;
  renderNavbar(container);

  const user = await getCurrentUser();
  if (!user) {
    window.location.hash = '#/login';
    return;
  }

  let profile = null;
  let debriefCount = 0;
  let friends = [];
  let pending = [];
  let commentCount = 0;
  let reactionCount = 0;
  let goals = [];

  try {
    const { data: pData, error: pErr } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    if (pErr) throw pErr;
    profile = await ensureFriendCode(pData);
    const friendCard = buildFriendCard(profile);
    const friendQr = await QRCode.toDataURL(friendCard, { errorCorrectionLevel: 'M', margin: 2, width: 260 });

    const { count: dCount } = await supabase
      .from('debriefs')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', user.id);
    debriefCount = dCount || 0;

    const { data: fData } = await supabase
      .from('friendships')
      .select(`
        *,
        requester:profiles!friendships_requester_id_fkey (id, pseudo, avatar_url, avatar_emoji),
        addressee:profiles!friendships_addressee_id_fkey (id, pseudo, avatar_url, avatar_emoji)
      `)
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`)
      .eq('status', 'accepted');
    friends = fData || [];

    const { data: peData } = await supabase
      .from('friendships')
      .select('*, requester:profiles!friendships_requester_id_fkey (id, pseudo, avatar_url, avatar_emoji)')
      .eq('addressee_id', user.id)
      .eq('status', 'pending');
    pending = peData || [];

    const [gData, cCount, rCount] = await Promise.all([
      safeSupabase(() => supabase
        .from('user_goals')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(3)),
      safeSupabase(() => supabase
        .from('comments')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)),
      safeSupabase(() => supabase
        .from('reactions')
        .select('debrief:debriefs!inner(user_id)', { count: 'exact', head: true })
        .eq('debrief.user_id', user.id))
    ]);
    goals = gData?.data || [];
    commentCount = cCount?.count || 0;
    reactionCount = rCount?.count || 0;

    const stats = {
      debriefs: debriefCount,
      comments: commentCount,
      reactionsReceived: reactionCount,
      quizEntries: 0
    };
    const basePoints = calculateBasePoints(stats);
    const level = levelFromPoints(basePoints);
    const activeGoals = Array.isArray(goals) ? goals : [];

    const content = container.querySelector('#profile-content');
    content.innerHTML = `
      <div class="flex-col gap-16">
        <!-- Profil -->
        <div class="glass-card text-center">
          <div id="profile-avatar" style="width:80px;height:80px;border-radius:50%;background:linear-gradient(135deg,var(--accent),var(--secondary));display:flex;align-items:center;justify-content:center;font-weight:700;font-size:28px;color:#fff;margin:0 auto 12px;">
            ${renderAvatarInner(profile)}
          </div>
          <h2 class="heading-2" data-profile-pseudo style="margin-bottom:4px;">@${escapeHtml(profile?.pseudo || t('userFallback'))}</h2>
          <p class="text-muted">${t('analysisCount', { count: formatNumber(debriefCount || 0, { maximumFractionDigits: 0 }), plural: (debriefCount || 0) > 1 ? 's' : '' })}</p>
          <div style="margin-top:12px;padding:12px;background:var(--surface-2);border-radius:8px;">
            <p style="margin:0;font-weight:700;">${t('levelLine', { level: level.level, label: escapeHtml(formatLevelLabel(level.label)) })}</p>
            <p class="text-muted" style="margin:4px 0 8px;font-size:12px;">${t('tacticalPoints', { points: formatNumber(basePoints, { maximumFractionDigits: 0 }) })}</p>
            <div style="height:8px;background:var(--border);border-radius:999px;overflow:hidden;">
              <div style="height:100%;width:${Math.round((level.current / level.next) * 100)}%;background:linear-gradient(90deg,var(--primary),var(--secondary));"></div>
            </div>
          </div>
          <p class="text-muted" style="font-size:12px;margin-top:8px;">
            ${t('analysesToday', { count: formatNumber(profile?.analyses_today || 0, { maximumFractionDigits: 0 }) })}
          </p>
          <div style="margin-top:14px;padding:12px;background:var(--surface-2);border-radius:8px;text-align:left;">
            <p style="font-weight:600;margin:0 0 8px;font-size:14px;">${t('username')}</p>
            <div style="display:flex;gap:8px;align-items:center;">
              <div style="position:relative;flex:1;">
                <span aria-hidden="true" style="position:absolute;left:12px;top:50%;transform:translateY(-50%);color:var(--muted);font-weight:700;">@</span>
                <input id="pseudo-input" class="input-glow" type="text" maxlength="${PSEUDO_MAX_LENGTH + 1}" autocapitalize="none" autocomplete="nickname" value="${escapeHtml(profile?.pseudo || '')}" style="width:100%;padding-left:28px;" />
              </div>
              <button id="btn-save-pseudo" class="btn-ghost" type="button">${t('save')}</button>
            </div>
            <p class="text-muted" style="font-size:12px;margin:8px 0 0;">${t('pseudoHelp')}</p>
          </div>
          <div style="margin-top:12px;padding:12px;background:var(--surface-2);border-radius:8px;text-align:left;">
            <p style="font-weight:600;margin:0 0 8px;font-size:14px;">${t('profilePhoto')}</p>
            <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap;">
              <input id="avatar-file-input" type="file" accept="image/png,image/jpeg,image/webp,image/gif" style="display:none;" />
              <button id="btn-upload-avatar" class="btn-ghost" type="button">${t('uploadPhoto')}</button>
              <button id="btn-remove-avatar" class="btn-ghost" type="button" style="color:var(--danger);border-color:var(--danger);${profile?.avatar_url ? '' : 'display:none;'}">${t('remove')}</button>
            </div>
            <p class="text-muted" style="font-size:12px;margin:8px 0 0;">${t('avatarPhotoHelp')}</p>
          </div>
          <div style="margin-top:12px;padding:12px;background:var(--surface-2);border-radius:8px;text-align:left;">
            <p style="font-weight:600;margin:0 0 8px;font-size:14px;">${t('avatarEmoji')}</p>
            <div style="display:flex;gap:8px;align-items:center;">
              <input id="avatar-emoji-input" class="input-glow" type="text" maxlength="16" inputmode="text" autocomplete="off" placeholder="${t('avatarEmojiPlaceholder')}" value="${escapeHtml(profile?.avatar_emoji || '')}" style="flex:1;text-align:center;font-size:20px;" />
              <button id="btn-save-avatar" class="btn-ghost" type="button">${t('save')}</button>
            </div>
            <p class="text-muted" style="font-size:12px;margin:8px 0 0;">${t('avatarEmojiHelp')}</p>
          </div>
          <div class="actions" style="justify-content:center;margin-top:12px;">
            <a class="btn-secondary" href="#/leaderboards">${t('viewRankings')}</a>
            <a class="btn-secondary" href="#/quiz">${t('activeQuiz')}</a>
          </div>
        </div>

        <!-- Objectifs -->
        <div class="glass-card">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px;">
            <p style="font-weight:600;margin:0;">${t('personalGoals')}</p>
            <span class="text-muted" style="font-size:12px;">${t('mvpPoints')}</span>
          </div>
          ${activeGoals.length ? `
            <div class="flex-col gap-12" style="margin-bottom:12px;">
              ${activeGoals.map(g => renderGoal(g, stats)).join('')}
            </div>
          ` : `<p class="text-muted" style="margin-bottom:12px;">${t('noActiveGoal')}</p>`}
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <select id="goal-preset" class="input-glow" style="flex:1;min-width:180px;">
              ${GOAL_PRESETS.map(g => `<option value="${escapeHtml(g.type)}">${escapeHtml(formatGoalLabel(g))}</option>`).join('')}
            </select>
            <button id="btn-create-goal" class="btn-ghost" type="button">${t('activate')}</button>
          </div>
          <p class="text-muted" style="font-size:12px;margin:10px 0 0;">${t('pointsScale')}</p>
        </div>

        <!-- Rechercher un ami -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:12px;">${t('searchFriend')}</p>
          <div style="display:flex;gap:8px;">
            <input id="search-pseudo" class="input-glow" type="text" placeholder="${t('pseudoPlaceholder')}" style="flex:1;" />
            <button id="btn-search" class="btn-ghost">${t('search')}</button>
          </div>
          <textarea id="friend-card-text" class="input-glow keycard-textarea" placeholder="${tr('Colle ici un QR ami décodé ou lis un tag NFC', 'Paste a decoded friend QR here or read an NFC tag')}" style="margin-top:12px;"></textarea>
          <button id="btn-add-friend-card" class="btn-primary" type="button" style="width:100%;margin-top:8px;">${tr('Ajouter via QR ami', 'Add from friend QR')}</button>
          <div id="search-results" style="margin-top:12px;"></div>
        </div>

        <!-- Demandes en attente -->
        ${pending?.length > 0 ? `
          <div class="glass-card">
            <p style="font-weight:600;margin-bottom:12px;">${t('pendingRequests', { count: pending.length })}</p>
            <div class="flex-col gap-12">
              ${pending.map(r => `
                <div style="display:flex;align-items:center;justify-content:space-between;">
                  <span style="font-weight:500;">@${escapeHtml(r.requester?.pseudo || '?')}</span>
                  <div style="display:flex;gap:8px;">
                    <button class="btn-ghost" style="padding:6px 12px;font-size:13px;" onclick="acceptFriend('${escapeHtml(r.id)}', this)">${t('accept')}</button>
                    <button class="btn-ghost" style="padding:6px 12px;font-size:13px;" onclick="rejectFriend('${escapeHtml(r.id)}', this)">${t('reject')}</button>
                  </div>
                </div>
              `).join('')}
            </div>
          </div>
        ` : ''}

        <!-- Liste d'amis -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:12px;">${t('friends', { count: friends?.length || 0 })}</p>
          ${friends?.length > 0 ? `
            <div class="flex-col gap-12">
              ${friends.map(f => {
                const friend = f.requester_id === user.id ? f.addressee : f.requester;
                return `
                  <div style="display:flex;align-items:center;gap:10px;">
                    <div style="width:36px;height:36px;border-radius:50%;background:linear-gradient(135deg,var(--accent),var(--secondary));display:flex;align-items:center;justify-content:center;font-weight:700;font-size:14px;color:#fff;">
                      ${renderAvatarInner(friend)}
                    </div>
                    <span style="font-weight:500;">@${escapeHtml(friend?.pseudo || '?')}</span>
                  </div>
                `;
              }).join('')}
            </div>
          ` : `<p class="text-muted">${t('noFriends')}</p>`}
        </div>

        <!-- Aide -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:8px;">${t('appGuide')}</p>
          <p class="text-muted" style="margin-bottom:12px;">${t('appGuideCopy')}</p>
          <button id="btn-open-tutorial" class="btn-secondary" type="button" style="width:100%;">${t('openGuide')}</button>
        </div>

        <!-- Keycard privée -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:8px;">Keycard privée</p>
          <p class="text-muted" style="margin-bottom:12px;">Sauvegarde ton accès dans un QR ou un fichier. Phrase secrète optionnelle : vide = accès direct, remplie = keycard chiffrée.</p>
          <div style="display:flex;gap:8px;flex-wrap:wrap;">
            <input id="profile-keycard-passphrase" class="input-glow" type="password" placeholder="Phrase secrète (optionnel)" style="flex:1;min-width:180px;" />
            <button id="btn-profile-show-keycard" class="btn-secondary" type="button">Afficher QR</button>
            <button id="btn-profile-download-keycard" class="btn-secondary" type="button">Télécharger</button>
          </div>
          <div id="profile-keycard-output" style="margin-top:12px;"></div>
        </div>

        <!-- QR ami public -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:8px;">${tr('QR ami public', 'Public friend QR')}</p>
          <div class="keycard-result-card">
            <img class="qr-preview" src="${friendQr}" alt="QR ami public" />
            <div>
              <p class="text-muted" style="margin-bottom:12px;">${tr("Ce QR sert seulement à t'ajouter comme ami. Il ne contient pas ta keycard de connexion.", 'This QR only lets someone add you as a friend. It does not contain your login keycard.')}</p>
              <div class="actions">
                <button id="btn-share-friend-card" class="btn-primary" type="button">${tr('Partager', 'Share')}</button>
                <button id="btn-nfc-write" class="btn-secondary" type="button">${tr('Écrire NFC', 'Write NFC')}</button>
                <button id="btn-nfc-read" class="btn-secondary" type="button">${tr('Lire NFC', 'Read NFC')}</button>
              </div>
              <p id="nfc-status" class="text-muted" style="font-size:12px;margin-top:10px;"></p>
            </div>
          </div>
        </div>

        <!-- Préférences -->
        <div class="glass-card">
          <p style="font-weight:600;margin-bottom:12px;">${t('preferences')}</p>
          <div style="margin-bottom:12px;">
            <p class="text-muted" style="font-size:12px;margin:0 0 8px;">${t('language')}</p>
            <div class="theme-toggle" id="language-toggle">
              ${supportedLanguages().map((language) => `<button type="button" data-language-value="${escapeHtml(language)}">${escapeHtml(languageLabel(language))}</button>`).join('')}
            </div>
          </div>
          <p class="text-muted" style="font-size:12px;margin:0 0 8px;">${t('appearance')}</p>
          <div class="theme-toggle" id="theme-toggle">
            <button type="button" data-theme-value="light">${t('light')}</button>
            <button type="button" data-theme-value="dark">${t('dark')}</button>
          </div>
        </div>

        <!-- Déconnexion -->
        <button id="btn-logout" class="btn-ghost" style="width:100%;color:var(--danger);border-color:var(--danger);">${t('logout')}</button>
      </div>
    `;

    // Sélecteur de thème
    const themeToggle = content.querySelector('#theme-toggle');
    const syncThemeButtons = () => {
      const active = getTheme();
      themeToggle.querySelectorAll('button').forEach(b => {
        b.classList.toggle('active', b.dataset.themeValue === active);
      });
    };
    syncThemeButtons();
    themeToggle.querySelectorAll('button').forEach(b => {
      b.addEventListener('click', () => {
        setTheme(b.dataset.themeValue);
        syncThemeButtons();
      });
    });

    const languageToggle = content.querySelector('#language-toggle');
    if (languageToggle) {
      const syncLanguageButtons = () => {
        const active = getLanguage();
        languageToggle.querySelectorAll('button').forEach(b => {
          b.classList.toggle('active', b.dataset.languageValue === active);
        });
      };
      syncLanguageButtons();
      languageToggle.querySelectorAll('button').forEach(b => {
        b.addEventListener('click', () => {
          if (b.dataset.languageValue === getLanguage()) return;
          setLanguage(b.dataset.languageValue);
          renderProfile(container);
        });
      });
    }

    const tutorialBtn = content.querySelector('#btn-open-tutorial');
    if (tutorialBtn) {
      tutorialBtn.addEventListener('click', () => {
        openAppTutorial({
          mode: 'manual',
          onClose: () => markProfileTutorialSeen(user.id)
        });
      });
    }

    const keycardOutput = content.querySelector('#profile-keycard-output');
    const keycardPassphrase = content.querySelector('#profile-keycard-passphrase');
    content.querySelector('#btn-profile-show-keycard').addEventListener('click', async () => {
      try {
        const keycard = await exportCurrentKeycard(keycardPassphrase.value);
        const qr = await QRCode.toDataURL(keycard, { errorCorrectionLevel: 'M', margin: 2, width: 260 });
        keycardOutput.innerHTML = `<img class="qr-preview" src="${qr}" alt="QR keycard privée">`;
      } catch (e) {
        showToast('Keycard impossible: ' + e.message);
      }
    });
    content.querySelector('#btn-profile-download-keycard').addEventListener('click', async () => {
      try {
        await downloadCurrentKeycard(keycardPassphrase.value);
        showToast('Keycard téléchargée.');
      } catch (e) {
        showToast('Téléchargement impossible: ' + e.message);
      }
    });

    content.querySelector('#btn-share-friend-card').addEventListener('click', async () => {
      await shareFriendCard(friendCard);
    });
    content.querySelector('#btn-nfc-write').addEventListener('click', () => writeNfcFriendCard(friendCard, content));
    content.querySelector('#btn-nfc-read').addEventListener('click', () => readNfcFriendCard(content));

    // Nom d'utilisateur
    const pseudoInput = content.querySelector('#pseudo-input');
    const pseudoBtn = content.querySelector('#btn-save-pseudo');
    if (pseudoInput && pseudoBtn) {
      pseudoBtn.addEventListener('click', async () => {
        const { pseudo, error: validationError } = validatePseudo(pseudoInput.value);
        if (validationError) {
          showToast(validationError);
          return;
        }
        if (pseudo === profile.pseudo) {
          showToast(t('pseudoUpToDate'));
          return;
        }
        pseudoBtn.disabled = true;
        const { data: updatedProfile, error } = await supabase
          .from('profiles')
          .update({ pseudo, updated_at: new Date().toISOString() })
          .eq('id', user.id)
          .select('pseudo,avatar_url,avatar_emoji')
          .maybeSingle();
        pseudoBtn.disabled = false;
        if (error) {
          if (error.code === '23505') {
            showToast(t('pseudoTaken'));
            return;
          }
          showToast(t('pseudoNotUpdated', { message: error.message }));
          return;
        }
        if (!updatedProfile) {
          showToast(t('pseudoLocked'));
          return;
        }
        try {
          await supabase.auth.updateUser({ data: { pseudo, name: pseudo, full_name: pseudo } });
        } catch {
          // Le profil public est la source d'affichage. Les métadonnées Auth sont
          // seulement synchronisées pour éviter un retour visuel à l'ancien nom.
        }
        profile = { ...profile, ...updatedProfile };
        pseudoInput.value = updatedProfile.pseudo;
        const title = content.querySelector('[data-profile-pseudo]');
        if (title) title.textContent = `@${updatedProfile.pseudo}`;
        refreshAvatarPreview();
        showToast(t('pseudoUpdated'));
      });
    }

    // Search
    const searchInput = content.querySelector('#search-pseudo');
    content.querySelector('#btn-search').addEventListener('click', async () => {
      const pseudo = searchInput.value.trim();
      if (!pseudo) return;
      const { data: results } = await supabase.from('profiles').select('id, pseudo, avatar_url, avatar_emoji').ilike('pseudo', `%${pseudo}%`).neq('id', user.id).limit(10);
      const resDiv = content.querySelector('#search-results');
      if (!results || results.length === 0) {
        resDiv.innerHTML = `<p class="text-muted">${t('noResults')}</p>`;
        return;
      }
      resDiv.innerHTML = results.map(r => `
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
          <span style="font-weight:500;">@${escapeHtml(r.pseudo)}</span>
          <button class="btn-ghost" style="padding:6px 12px;font-size:13px;" onclick="addFriend('${escapeHtml(r.id)}', this)">${t('add')}</button>
        </div>
      `).join('');
    });

    const friendCardText = content.querySelector('#friend-card-text');
    const addFriendCardBtn = content.querySelector('#btn-add-friend-card');
    if (friendCardText && addFriendCardBtn) {
      addFriendCardBtn.addEventListener('click', async () => {
        try {
          const card = parseFriendCard(friendCardText.value);
          await addFriendFromCard(card);
          showToast(tr('Demande envoyée.', 'Request sent.'));
        } catch (e) {
          showToast(e.message);
        }
      });
    }

    const goalSelect = content.querySelector('#goal-preset');
    const goalBtn = content.querySelector('#btn-create-goal');
    if (goalSelect && goalBtn) {
      goalBtn.addEventListener('click', async () => {
        const preset = goalPresetByType(goalSelect.value);
        goalBtn.disabled = true;
        const { error } = await supabase.from('user_goals').insert({
          user_id: user.id,
          goal_type: preset.type,
          target_value: preset.target
        });
        if (error) {
          showToast(t('goalsUnavailable'));
          goalBtn.disabled = false;
          return;
        }
        showToast(t('goalActivated'));
        setTimeout(() => window.location.reload(), 500);
      });
    }

    // Avatar emoji (repli quand aucune photo)
    const avatarInput = content.querySelector('#avatar-emoji-input');
    const avatarBtn = content.querySelector('#btn-save-avatar');
    const avatarPreview = content.querySelector('#profile-avatar');
    const refreshAvatarPreview = () => {
      if (avatarPreview) avatarPreview.innerHTML = renderAvatarInner(profile);
    };
    if (avatarInput && avatarBtn) {
      avatarBtn.addEventListener('click', async () => {
        const clean = sanitizeAvatarEmoji(avatarInput.value);
        avatarBtn.disabled = true;
        const { error } = await supabase
          .from('profiles')
          .update({ avatar_emoji: clean || null })
          .eq('id', user.id);
        avatarBtn.disabled = false;
        if (error) {
          showToast(t('avatarUnavailable'));
          return;
        }
        avatarInput.value = clean;
        profile.avatar_emoji = clean || null;
        refreshAvatarPreview();
        showToast(clean ? t('emojiUpdated') : t('emojiReset'));
      });
    }

    // Photo de profil (upload Supabase Storage)
    const fileInput = content.querySelector('#avatar-file-input');
    const uploadBtn = content.querySelector('#btn-upload-avatar');
    const removeBtn = content.querySelector('#btn-remove-avatar');
    if (fileInput && uploadBtn) {
      uploadBtn.addEventListener('click', () => fileInput.click());
      fileInput.addEventListener('change', async () => {
        const file = fileInput.files?.[0];
        if (!file) return;
        if (!AVATAR_ALLOWED_TYPES.includes(file.type)) {
          showToast(t('unsupportedFormat'));
          fileInput.value = '';
          return;
        }
        if (file.size > AVATAR_MAX_BYTES) {
          showToast(t('imageTooHeavy'));
          fileInput.value = '';
          return;
        }
        uploadBtn.disabled = true;
        const prevLabel = uploadBtn.textContent;
        uploadBtn.textContent = t('uploading');
        try {
          const ext = AVATAR_EXT_BY_TYPE[file.type] || 'png';
          const path = `${user.id}/avatar.${ext}`;
          // Nettoie les anciens fichiers (extension différente) pour éviter les orphelins.
          try {
            const { data: existing } = await supabase.storage.from('avatars').list(user.id);
            const stale = (existing || [])
              .filter((f) => f.name !== `avatar.${ext}`)
              .map((f) => `${user.id}/${f.name}`);
            if (stale.length) await supabase.storage.from('avatars').remove(stale);
          } catch { /* listing best-effort */ }

          const { error: upErr } = await supabase.storage
            .from('avatars')
            .upload(path, file, { upsert: true, contentType: file.type, cacheControl: '3600' });
          if (upErr) {
            showToast(t('uploadUnavailable'));
            return;
          }
          const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
          const url = `${pub.publicUrl}?v=${Date.now()}`;
          const { error } = await supabase
            .from('profiles')
            .update({ avatar_url: url })
            .eq('id', user.id);
          if (error) {
            showToast(t('profileNotUpdated', { message: error.message }));
            return;
          }
          profile.avatar_url = url;
          refreshAvatarPreview();
          if (removeBtn) removeBtn.style.display = '';
          showToast(t('photoUpdated'));
        } finally {
          uploadBtn.disabled = false;
          uploadBtn.textContent = prevLabel;
          fileInput.value = '';
        }
      });
    }
    if (removeBtn) {
      removeBtn.addEventListener('click', async () => {
        removeBtn.disabled = true;
        try {
          const { data: existing } = await supabase.storage.from('avatars').list(user.id);
          const all = (existing || []).map((f) => `${user.id}/${f.name}`);
          if (all.length) await supabase.storage.from('avatars').remove(all);
        } catch { /* suppression Storage best-effort */ }
        const { error } = await supabase
          .from('profiles')
          .update({ avatar_url: null })
          .eq('id', user.id);
        removeBtn.disabled = false;
        if (error) {
          showToast(t('deleteFailed', { message: error.message }));
          return;
        }
        profile.avatar_url = null;
        refreshAvatarPreview();
        removeBtn.style.display = 'none';
        showToast(t('photoDeleted'));
      });
    }

    // Logout
    content.querySelector('#btn-logout').addEventListener('click', async () => {
      await signOut();
      window.location.hash = '#/login';
    });

  } catch (e) {
    container.querySelector('#profile-content').innerHTML = `<p class="text-muted">${t('genericError', { message: escapeHtml(e.message) })}</p>`;
  }
}

async function safeSupabase(fn) {
  try {
    const res = await fn();
    if (res.error) return { data: null, count: 0, error: res.error };
    return res;
  } catch (e) {
    return { data: null, count: 0, error: e };
  }
}

function renderGoal(goal, stats) {
  const preset = goalPresetByType(goal.goal_type);
  const progress = progressForGoal(goal, stats);
  return `
    <div style="padding:10px 12px;background:var(--surface-2);border-radius:8px;">
      <div style="display:flex;justify-content:space-between;gap:8px;margin-bottom:6px;">
        <span style="font-weight:600;font-size:14px;">${escapeHtml(formatGoalLabel(preset))}</span>
        <span class="text-muted" style="font-size:12px;">${progress.current}/${progress.target}</span>
      </div>
      <div style="height:7px;background:var(--border);border-radius:999px;overflow:hidden;">
        <div style="height:100%;width:${progress.pct}%;background:var(--secondary);"></div>
      </div>
    </div>
  `;
}

function formatGoalLabel(goal) {
  const keyByType = {
    analyses_week: 'goalAnalysesWeek',
    comments_week: 'goalCommentsWeek',
    quiz_day: 'goalQuizDay'
  };
  return t(keyByType[goal?.type] || '', {}) || goal?.label || '';
}

function formatLevelLabel(label) {
  const keyByLabel = {
    'Analyste en herbe': 'levelRookie',
    'Analyste confirmé': 'levelConfirmed',
    'Analyste élite': 'levelElite'
  };
  return t(keyByLabel[label] || '', {}) || label || '';
}

function randomFriendCode() {
  const bytes = crypto.getRandomValues(new Uint8Array(18));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
}

async function ensureFriendCode(profile) {
  if (profile?.friend_code) return profile;
  const friendCode = randomFriendCode();
  const { data, error } = await supabase
    .from('profiles')
    .update({ friend_code: friendCode, updated_at: new Date().toISOString() })
    .eq('id', profile.id)
    .select('*')
    .maybeSingle();
  if (error) throw error;
  return data || { ...profile, friend_code: friendCode };
}

function buildFriendCard(profile) {
  return JSON.stringify({
    app: 'Fooview',
    type: 'friend-card',
    v: 1,
    userId: profile.id,
    pseudo: profile.pseudo || '',
    friendCode: profile.friend_code
  });
}

function parseFriendCard(value) {
  const card = JSON.parse(String(value || '').trim());
  if (card.app !== 'Fooview' || card.type !== 'friend-card' || card.v !== 1) {
    throw new Error(tr('QR ami Fooview invalide.', 'Invalid Fooview friend QR.'));
  }
  if (!card.userId || !card.friendCode) {
    throw new Error(tr('QR ami incomplet.', 'Incomplete friend QR.'));
  }
  return {
    userId: String(card.userId),
    friendCode: String(card.friendCode)
  };
}

async function shareFriendCard(friendCard) {
  if (navigator.share) {
    try {
      await navigator.share({ title: 'Fooview', text: friendCard });
      return;
    } catch {
      // Fallback clipboard below.
    }
  }
  await navigator.clipboard.writeText(friendCard);
  showToast(tr('QR ami copié.', 'Friend QR copied.'));
}

async function addFriendFromCard(card) {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error(tr('Non authentifié.', 'Not signed in.'));
  if (card.userId === user.id) throw new Error(tr('Tu ne peux pas t’ajouter toi-même.', 'You cannot add yourself.'));
  const { data: target, error: profileError } = await supabase
    .from('profiles')
    .select('id,friend_code')
    .eq('id', card.userId)
    .eq('friend_code', card.friendCode)
    .maybeSingle();
  if (profileError) throw profileError;
  if (!target) throw new Error(tr('QR ami expiré ou invalide.', 'Friend QR expired or invalid.'));
  const { error } = await supabase.from('friendships').insert({
    requester_id: user.id,
    addressee_id: target.id,
    status: 'pending'
  });
  if (error) throw error;
}

async function writeNfcFriendCard(friendCard, content) {
  const status = content.querySelector('#nfc-status');
  if (!('NDEFReader' in window)) {
    status.textContent = tr('Web NFC non disponible ici. Utilise Android Chrome en PWA.', 'Web NFC is not available here. Use Android Chrome as a PWA.');
    return;
  }
  try {
    const ndef = new NDEFReader();
    await ndef.write({ records: [{ recordType: 'text', data: friendCard }] });
    status.textContent = tr('QR ami écrit sur le tag NFC.', 'Friend QR written to the NFC tag.');
  } catch (e) {
    status.textContent = tr('NFC impossible: ', 'NFC failed: ') + e.message;
  }
}

async function readNfcFriendCard(content) {
  const status = content.querySelector('#nfc-status');
  if (!('NDEFReader' in window)) {
    status.textContent = tr('Web NFC non disponible ici. Utilise Android Chrome en PWA.', 'Web NFC is not available here. Use Android Chrome as a PWA.');
    return;
  }
  try {
    const ndef = new NDEFReader();
    await ndef.scan();
    status.textContent = tr('Approche un tag NFC Fooview...', 'Move close to a Fooview NFC tag...');
    ndef.addEventListener('reading', ({ message }) => {
      const record = message.records.find((item) => item.recordType === 'text');
      const text = record ? new TextDecoder(record.encoding || 'utf-8').decode(record.data) : '';
      content.querySelector('#friend-card-text').value = text;
      status.textContent = tr('Tag lu. Tu peux ajouter cet ami.', 'Tag read. You can add this friend.');
    });
  } catch (e) {
    status.textContent = tr('Lecture NFC impossible: ', 'NFC read failed: ') + e.message;
  }
}

window.acceptFriend = async (id, btn) => {
  const { supabase } = await import('../services/supabase.js');
  await supabase.from('friendships').update({ status: 'accepted' }).eq('id', id);
  btn.closest('.glass-card').style.opacity = '0.5';
  setTimeout(() => window.location.reload(), 500);
};

window.rejectFriend = async (id, btn) => {
  const { supabase } = await import('../services/supabase.js');
  await supabase.from('friendships').delete().eq('id', id);
  btn.closest('.glass-card').style.opacity = '0.5';
  setTimeout(() => window.location.reload(), 500);
};

window.addFriend = async (friendId, btn) => {
  const { supabase } = await import('../services/supabase.js');
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase.from('friendships').insert({
    requester_id: user.id,
    addressee_id: friendId,
    status: 'pending'
  });
  if (error) {
    showToast(t('genericError', { message: escapeHtml(error.message) }));
  } else {
    btn.textContent = t('sent');
    btn.disabled = true;
  }
};

function languageLabel(language) {
  const labels = {
    fr: t('french'),
    en: t('english'),
    es: t('spanish'),
    pt: t('portuguese')
  };
  return labels[language] || language.toUpperCase();
}

function showToast(msg) {
  const t = document.createElement('div');
  t.className = 'toast show';
  t.textContent = msg;
  document.body.appendChild(t);
  setTimeout(() => { t.className = 'toast'; setTimeout(() => t.remove(), 300); }, 3000);
}
